/**
 * FieldPalette — left rail of the visual builder.
 *
 * Renders the catalog returned by `GET /api/v1/workflows/fields/catalog`,
 * grouped by category, filterable by search term. Each card is a
 * `useDraggable` source from @dnd-kit; drop is wired in {@link ConditionDropZone}
 * (step canvas) and the trigger predicate row in {@link TriggerPanel}.
 *
 * Each row also exposes an accessible "+ Add to condition" / "+ Add to step
 * X" affordance via a small dropdown menu, so keyboard / no-pointer users
 * can author workflows without DnD.
 */

import { useCallback, useId, useMemo, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import {
  FIELD_CATEGORIES,
  type FieldCategory,
  type FieldType,
  type LoanFieldDescriptor,
} from '@mana/shared';
import { Badge } from '../../../ui/Card.js';

// ---------------------------------------------------------------------------
// Constants the canvas reads to identify drop sources.
// ---------------------------------------------------------------------------

export const FIELD_DRAG_PREFIX = 'field:';
export interface FieldDragPayload {
  kind: 'field';
  fieldId: string;
}

export function makeFieldDragId(fieldId: string): string {
  return `${FIELD_DRAG_PREFIX}${fieldId}`;
}

export function parseFieldDragId(dragId: string): string | null {
  if (!dragId.startsWith(FIELD_DRAG_PREFIX)) return null;
  return dragId.slice(FIELD_DRAG_PREFIX.length);
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface FieldPaletteProps {
  fields: readonly LoanFieldDescriptor[];
  /** Steps the user can target via the "+ Add to step" accessible fallback. */
  stepOptions: ReadonlyArray<{ key: string; name: string }>;
  /** Called when the user picks "Add to step X" from the field card's menu. */
  onAddFieldToStep: (fieldId: string, stepKey: string) => void;
  /** Called when the user picks "Add to trigger predicate". */
  onAddFieldToTrigger: (fieldId: string) => void;
}

export function FieldPalette({
  fields,
  stepOptions,
  onAddFieldToStep,
  onAddFieldToTrigger,
}: FieldPaletteProps) {
  const [query, setQuery] = useState('');

  const grouped = useMemo(() => groupFields(fields, query), [fields, query]);

  return (
    <aside
      className="flex h-full flex-col gap-3 border-r border-slate-200 bg-white/90 p-3"
      aria-label="Loan field palette"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-brand-navy-800">
          Fields
        </h3>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          drag onto a condition
        </span>
      </div>

      <label className="block">
        <span className="sr-only">Search fields</span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search fields…"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
        />
      </label>

      <div className="-mx-1 flex-1 overflow-y-auto pr-1">
        {grouped.length === 0 && (
          <p className="px-2 py-4 text-center text-xs text-slate-500">
            No fields match &ldquo;{query}&rdquo;.
          </p>
        )}
        {grouped.map(({ category, items }) => (
          <section key={category} className="mb-3">
            <h4 className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              {humanizeCategory(category)}
            </h4>
            <ul className="space-y-1.5">
              {items.map((field) => (
                <li key={field.id}>
                  <FieldCard
                    field={field}
                    stepOptions={stepOptions}
                    onAddToStep={(stepKey) => onAddFieldToStep(field.id, stepKey)}
                    onAddToTrigger={() => onAddFieldToTrigger(field.id)}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <p className="text-[10px] text-slate-400">
        UWM = pulled from UWM Insights. MANUAL = captured on the Pipeline drawer.
      </p>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// FieldCard — single draggable item.
// ---------------------------------------------------------------------------

function FieldCard({
  field,
  stepOptions,
  onAddToStep,
  onAddToTrigger,
}: {
  field: LoanFieldDescriptor;
  stepOptions: ReadonlyArray<{ key: string; name: string }>;
  onAddToStep: (stepKey: string) => void;
  onAddToTrigger: () => void;
}) {
  const dragId = makeFieldDragId(field.id);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    data: { kind: 'field', fieldId: field.id } satisfies FieldDragPayload,
  });

  const tooltipId = useId();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleAdd = useCallback(
    (stepKey: string) => {
      onAddToStep(stepKey);
      setMenuOpen(false);
    },
    [onAddToStep],
  );

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      aria-describedby={tooltipId}
      title={field.description}
      className={clsx(
        'group relative cursor-grab select-none rounded-lg border bg-white px-2 py-1.5 text-left shadow-sm transition-all',
        'hover:border-brand-navy-300 hover:shadow-soft',
        isDragging
          ? 'border-brand-gold-400 opacity-50 ring-2 ring-brand-gold-200'
          : 'border-slate-200',
      )}
    >
      <div className="flex items-start justify-between gap-1.5">
        <span className="line-clamp-2 text-xs font-medium text-brand-navy-800">
          {field.label}
        </span>
        <SourceBadge source={field.source} />
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-1.5">
        <TypeBadge type={field.type} />
        <FieldRowMenu
          fieldLabel={field.label}
          stepOptions={stepOptions}
          open={menuOpen}
          onOpenChange={setMenuOpen}
          onAddToStep={handleAdd}
          onAddToTrigger={() => {
            onAddToTrigger();
            setMenuOpen(false);
          }}
        />
      </div>
      <span id={tooltipId} className="sr-only">
        {field.description}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Accessibility-fallback menu — keyboard alternative to drag.
// ---------------------------------------------------------------------------

function FieldRowMenu({
  fieldLabel,
  stepOptions,
  open,
  onOpenChange,
  onAddToStep,
  onAddToTrigger,
}: {
  fieldLabel: string;
  stepOptions: ReadonlyArray<{ key: string; name: string }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddToStep: (stepKey: string) => void;
  onAddToTrigger: () => void;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(!open);
        }}
        // pointerDown handler stops dnd-kit's PointerSensor from picking up
        // the card while the user is interacting with the menu button.
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.stopPropagation();
          }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Add ${fieldLabel} to a condition (keyboard alternative)`}
        className="rounded px-1 py-0.5 text-[10px] text-slate-400 hover:bg-slate-100 hover:text-brand-navy-700"
      >
        + Add
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-56 rounded-lg border border-slate-200 bg-white p-1 shadow-floating"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              onAddToTrigger();
            }}
            className="block w-full rounded-md px-2 py-1 text-left text-xs text-brand-navy-800 hover:bg-brand-navy-50"
          >
            Add to trigger predicate
          </button>
          <div className="my-1 border-t border-slate-100" />
          {stepOptions.length === 0 && (
            <p className="px-2 py-1 text-[10px] text-slate-400">
              No steps yet — add one first.
            </p>
          )}
          {stepOptions.map((s) => (
            <button
              key={s.key}
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                onAddToStep(s.key);
              }}
              className="block w-full truncate rounded-md px-2 py-1 text-left text-xs text-slate-700 hover:bg-slate-100"
            >
              Add to <span className="font-medium">{s.name || s.key}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small badges
// ---------------------------------------------------------------------------

function SourceBadge({ source }: { source: LoanFieldDescriptor['source'] }) {
  if (source === 'uwm') {
    return (
      <Badge tone="info" className="!px-1 !text-[9px]">
        UWM
      </Badge>
    );
  }
  if (source === 'derived') {
    return (
      <Badge tone="gold" className="!px-1 !text-[9px]">
        DERIVED
      </Badge>
    );
  }
  return (
    <Badge tone="warning" className="!px-1 !text-[9px]">
      MANUAL
    </Badge>
  );
}

function TypeBadge({ type }: { type: FieldType }) {
  const label = TYPE_LABELS[type];
  return (
    <span className="rounded bg-slate-100 px-1 py-px text-[9px] font-medium uppercase tracking-wider text-slate-500">
      {label}
    </span>
  );
}

const TYPE_LABELS: Record<FieldType, string> = {
  string: 'text',
  number: 'number',
  integer: 'integer',
  date: 'date',
  datetime: 'datetime',
  boolean_yn: 'Y/N',
  enum: 'choice',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupFields(
  fields: readonly LoanFieldDescriptor[],
  query: string,
): { category: FieldCategory; items: LoanFieldDescriptor[] }[] {
  const normalized = query.trim().toLowerCase();
  const byCategory = new Map<FieldCategory, LoanFieldDescriptor[]>();
  for (const f of fields) {
    if (
      normalized &&
      !f.label.toLowerCase().includes(normalized) &&
      !f.description.toLowerCase().includes(normalized) &&
      !f.id.toLowerCase().includes(normalized)
    ) {
      continue;
    }
    const list = byCategory.get(f.category) ?? [];
    list.push(f);
    byCategory.set(f.category, list);
  }
  return FIELD_CATEGORIES
    .map((category) => ({ category, items: byCategory.get(category) ?? [] }))
    .filter((g) => g.items.length > 0);
}

function humanizeCategory(category: FieldCategory): string {
  switch (category) {
    case 'loan':
      return 'Loan identity';
    case 'borrower':
      return 'Borrowers';
    case 'parties':
      return 'Non-borrowing parties';
    case 'status':
      return 'Status';
    case 'lock':
      return 'Rate lock';
    case 'conditions':
      return 'Conditions';
    case 'docs':
      return 'Disclosures & signatures';
    case 'dates':
      return 'Sync metadata';
    case 'closing':
      return 'Closing & funding';
  }
}
